import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck, RefreshCw, Headphones, ChevronLeft, ChevronRight, Star, Quote, PawPrint, Leaf, Heart, Sparkles, ShieldCheck, BadgeCheck } from 'lucide-react';
import { Banners, Categories, Products, Blog, api } from '../lib/api';
import { imgUrl, imgSrcSet } from '../lib/img';
import { BRAND } from '../brand';
import { useSeo } from '../hooks/useSeo';
import ProductCard from '../components/ProductCard';
import InstagramFeed from '../components/InstagramFeed';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const TESTIMONIALS = [
  { name: 'Pragathi Guru', rating: 5, text: 'Rare Locals has become my constant companion through busy days. The aroma and flavour are consistently excellent.' },
  { name: 'Mani Megalai', rating: 4.5, text: 'A rich and refreshing cup. It gives me the perfect recharge whenever I need a break during the day.' },
  { name: 'Alya Manasa', rating: 5, text: 'The flavour is smooth, balanced and satisfying. Rare Locals has quickly become one of my favourite brands.' },
  { name: 'Megha Sharma', rating: 4.5, text: 'Strong, aromatic and easy to prepare. This is exactly the kind of coffee I enjoy every morning.' },
];

const FAQS = [
  { q: 'How long does shipping take?', a: 'Orders typically arrive within 3–7 business days depending on your location. Peak seasons and remote areas may see slightly longer times.' },
  { q: 'When will I get my refund?', a: 'Refunds are processed within 5–10 business days after your return is approved. Exact timing may vary depending on your bank.' },
  { q: 'How can I track my order?', a: 'Once your order ships, you’ll get an email with a tracking link. You can also view the status inside your account.' },
  { q: 'Do you offer gift cards?', a: 'Yes! Digital gift cards are delivered via email and include easy instructions for use. Perfect for any occasion.' },
  { q: 'Do you ship internationally?', a: 'We currently focus on Pan-India delivery. International shipping is coming soon to select countries.' },
];

function HeroCarousel({ banners }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!banners.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  // Always render hero container to prevent layout flash while banners load
  if (!banners.length) {
    return (
      <section className="relative w-full bg-brand-dark overflow-hidden">
        <div className="w-full aspect-[4/3] sm:aspect-[21/9] bg-gradient-to-br from-brand-dark via-brand-green to-brand-dark" />
        <div className="absolute inset-0 z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-xl text-brand-gold-light">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-3">Rare Locals</div>
            <h1 className="font-serif-brand text-3xl sm:text-4xl md:text-6xl font-semibold text-white leading-tight">
              Rare in Nature. Local at Heart.
            </h1>
          </div>
        </div>
      </section>
    );
  }
  const b = banners[idx];
  const prev = () => setIdx((i) => (i - 1 + banners.length) % banners.length);
  const next = () => setIdx((i) => (i + 1) % banners.length);
  const arrowCls = 'w-10 h-10 rounded-full border border-brand-gold-light/40 text-brand-gold-light hover:bg-brand-gold hover:text-brand-dark transition-colors flex items-center justify-center bg-brand-dark/50 backdrop-blur';
  const Dots = ({ prefix = 'hero-dot' }) => (
    <div className="flex gap-2">
      {banners.map((_, i) => (
        <button key={i} aria-label={`Banner ${i + 1}`} data-testid={`${prefix}-${i}`} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? 'bg-brand-gold w-8' : 'bg-brand-gold-light/40 w-3'}`} />
      ))}
    </div>
  );

  return (
    <section className="relative w-full bg-brand-dark" data-testid="hero-carousel">
      {/* One full-width banner on every screen — the copy sits on the artwork.
          Ratios are kept close to the 3:2 source so nothing is zoom-cropped:
          5:4 on phones, 16:9 from tablet up (capped so it never eats the screen). */}
      <div className={`relative w-full ${b.mobile_image ? 'aspect-[4/5]' : 'aspect-[3/2]'} sm:aspect-[16/9] lg:aspect-[2.2/1] max-h-[70vh] mx-auto bg-[#ece1c9] overflow-hidden`}>
        {banners.map((bn, i) => (
          <img
            key={bn.id || i}
            src={imgUrl(bn.image, 1600)}
            srcSet={imgSrcSet(bn.image)}
            sizes="100vw"
            alt={bn.title}
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'auto'}
            data-testid={`hero-image-${i}`}
            className={`absolute inset-0 w-full h-full ${b.mobile_image ? 'hidden sm:block' : 'object-contain'} sm:object-cover object-center transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
          />
        ))}
        {/* Optional phone-specific artwork, layered above on small screens only */}
        {b.mobile_image && (
          <img src={imgUrl(b.mobile_image, 800)} alt={b.title} className="sm:hidden absolute inset-0 w-full h-full object-cover object-center" data-testid="hero-image-mobile" />
        )}

        {/* Readability scrim — a bottom band only, so the product artwork above stays clear */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, rgba(13,40,24,0.96) 0%, rgba(13,40,24,0.90) 24%, rgba(13,40,24,0.55) 42%, rgba(13,40,24,0.10) 58%, rgba(13,40,24,0) 70%)',
          }}
        />

        <div className="absolute inset-0 z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-5 sm:pb-8 lg:pb-10">
          <div className="w-full sm:max-w-[62%] text-brand-gold-light fade-up" key={idx}>
            <div className="hidden sm:block text-[11px] uppercase tracking-[0.3em] text-brand-gold mb-3">Rare Locals • Fresh from the source</div>
            <h1 className="font-serif-brand text-[22px] sm:text-3xl lg:text-4xl xl:text-[44px] font-semibold text-white leading-[1.1] drop-shadow-[0_2px_12px_rgba(13,40,24,0.55)]">
              {b.title}
            </h1>
            <p className="mt-1 sm:mt-3 text-brand-gold-light/95 text-[12px] sm:text-sm lg:text-base font-medium line-clamp-1 sm:line-clamp-2">{b.subtitle}</p>
            <div className="mt-2.5 sm:mt-6 flex flex-wrap gap-2 sm:gap-3">
              <Link to={b.link || '/shop'}>
                <Button className="btn-gold rounded-full h-8 sm:h-11 px-4 sm:px-7 text-xs sm:text-sm font-medium">{b.cta_text || 'Shop Now'}</Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="rounded-full h-8 sm:h-11 px-3.5 sm:px-6 text-xs sm:text-sm bg-brand-dark/25 backdrop-blur-[2px] border-brand-gold-light/50 text-brand-gold-light hover:bg-brand-gold hover:text-brand-dark hover:border-brand-gold">
                  Our Story
                </Button>
              </Link>
            </div>
            <div className="mt-2.5 sm:mt-7">
              <Dots />
            </div>
          </div>
        </div>

        {/* Arrows — anchored to the banner edge so they never steal copy space */}
        <div className="absolute z-10 bottom-4 right-4 sm:bottom-8 sm:right-6 flex items-center gap-2">
          <button aria-label="Previous banner" data-testid="hero-prev" onClick={prev} className={arrowCls}><ChevronLeft size={16} /></button>
          <button aria-label="Next banner" data-testid="hero-next" onClick={next} className={arrowCls}><ChevronRight size={16} /></button>
        </div>
      </div>
    </section>
  );
}


function RareDropsFeed() {
  const [data, setData] = React.useState(null);
  const [profile, setProfile] = React.useState(null);
  React.useEffect(() => {
    let tp = null;
    try {
      const raw = localStorage.getItem('rl_taste_profile');
      if (raw) tp = JSON.parse(raw);
    } catch {}
    setProfile(tp);
    api.post('/ai/drops', { taste_profile: tp })
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, []);
  if (!data || !data.drops?.length) return null;
  return (
    <section id="rare-drops" data-testid="rare-drops" className="bg-brand-ivory py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <span className="inline-flex items-center gap-2 text-brand-gold text-[11px] uppercase tracking-[0.28em] font-semibold">
              <Sparkles size={13} strokeWidth={2.5} /> {profile ? 'Rare Drops · For You' : 'Rare Drops'}
            </span>
            <h2 className="mt-3 font-serif-brand text-3xl sm:text-4xl text-brand-dark leading-tight" style={{ letterSpacing: '-0.01em' }}>
              {data.heading}
            </h2>
            <p className="mt-2 text-brand-green/75 text-sm max-w-xl">{data.message}</p>
          </div>
          {!profile && (
            <Link to="/discover" className="inline-flex items-center gap-1 h-10 px-5 rounded-full border border-brand-gold/40 text-brand-dark hover:bg-white text-xs font-medium">
              Personalise this <ChevronRight size={12} />
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {data.drops.slice(0, 8).map(({ product, hook }) => (
            <Link key={product.id} to={`/product/${product.slug}`} data-testid={`drop-${product.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-brand-gold/10 hover:border-brand-gold/40 transition-all">
              <div className="aspect-square bg-brand-ivory overflow-hidden">
                <img loading="lazy" src={product.images?.[0]} alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
              </div>
              <div className="p-3 sm:p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] text-brand-gold font-semibold line-clamp-1">{hook}</p>
                <p className="mt-1 text-sm text-brand-dark font-medium line-clamp-1">{product.name}</p>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-sm font-semibold text-brand-dark">₹{product.price}</span>
                  {product.compare_at_price && product.compare_at_price > product.price && (
                    <span className="text-[10px] text-brand-green/60 line-through">₹{product.compare_at_price}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}



function PetCorner() {
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    // Load one hero product per pet sub-category to make each card feel alive.
    Promise.all([
      Products.list({ category: 'pet-dehydrated-treats', limit: 1 }),
      Products.list({ category: 'pet-meal-toppers', limit: 1 }),
      Products.list({ category: 'pet-daily-meals', limit: 1 }),
    ])
      .then(([a, b, c]) => setItems([
        { slug: 'pet-dehydrated-treats', label: 'Dehydrated Treats', tag: 'Small-batch', product: (a.items || [])[0] },
        { slug: 'pet-meal-toppers',      label: 'Meal Toppers',      tag: 'Boosters',     product: (b.items || [])[0] },
        { slug: 'pet-daily-meals',       label: 'Daily Meals',       tag: 'Complete',     product: (c.items || [])[0] },
      ]))
      .catch(() => {});
  }, []);

  return (
    <section
      id="pet-corner"
      data-testid="pet-corner"
      className="relative overflow-hidden py-16 sm:py-20"
    >
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#f6efe1] via-[#efe0c5] to-[#f9f3e2]" />
      <div aria-hidden="true" className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-brand-green/10 blur-3xl" />
      <div aria-hidden="true" className="absolute -top-16 right-1/4 w-72 h-72 rounded-full bg-brand-gold/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 h-8 pl-3 pr-4 rounded-full bg-brand-dark text-brand-gold text-[10px] uppercase tracking-[0.24em] font-semibold">
              <PawPrint size={12} strokeWidth={2.5} /> Pet Corner
            </div>
            <h2 className="mt-4 font-serif-brand text-3xl sm:text-4xl lg:text-5xl text-brand-dark leading-[1.05]" style={{ letterSpacing: '-0.01em' }}>
              Everything for the four-legged<br className="hidden sm:block" /> member of your family.
            </h2>
            <p className="mt-3 text-brand-dark/70 text-[15px] max-w-xl leading-relaxed">
              Slow-dried treats, nutrient-dense toppers and complete daily bowls — sourced and made with the same care as everything we sell.
            </p>
          </div>
          <Link to="/shop/pet-food" data-testid="pet-corner-shop-all"
            className="inline-flex self-start sm:self-end items-center gap-1.5 h-11 px-5 rounded-full bg-brand-dark text-brand-gold text-xs font-semibold hover:bg-brand-dark/90 transition-colors">
            Shop the range <ChevronRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {items.map(({ slug, label, tag, product }) => (
            <Link key={slug} to={`/shop/${slug}`} data-testid={`pet-corner-${slug}`}
              className="group relative gold-foil-edge bg-white rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_25px_60px_-15px_rgba(28,40,24,0.25)]">
              <div className="aspect-[4/3] bg-brand-ivory overflow-hidden">
                {product?.images?.[0] ? (
                  <img loading="lazy" src={product.images[0]} alt=""
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PawPrint size={48} className="text-brand-gold/30" strokeWidth={1.5} />
                  </div>
                )}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur text-brand-dark text-[10px] uppercase tracking-[0.16em] font-semibold px-2.5 py-1 rounded-full">
                  {tag}
                </div>
              </div>
              <div className="p-5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-brand-gold font-semibold">Explore</div>
                  <div className="mt-0.5 font-serif-brand text-xl text-brand-dark leading-tight line-clamp-1">{label}</div>
                </div>
                <span className="w-9 h-9 flex-shrink-0 rounded-full border border-brand-gold/30 flex items-center justify-center text-brand-gold group-hover:bg-brand-gold group-hover:text-brand-dark group-hover:border-brand-gold transition-all">
                  <ChevronRight size={16} strokeWidth={2.2} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


function PetFoodSpotlight() {
  const perks = [
    { Icon: Leaf, title: 'All-natural', text: 'Human-grade ingredients, no fillers, no synthetic preservatives.' },
    { Icon: Heart, title: 'Vet-friendly', text: 'Recipes small enough that we know every ingredient.' },
    { Icon: Sparkles, title: 'Freshly baked', text: 'Made in small batches every week, never mass-produced.' },
  ];
  return (
    <section
      id="pet-food-spotlight"
      data-testid="pet-food-spotlight"
      className="relative overflow-hidden py-10 sm:py-16 lg:py-20"
    >
      {/* Background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-[#f6efe1] via-[#efe0c5] to-[#f9f3e2]"
      />
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-gold/15 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        {/* Image side */}
        <div className="lg:col-span-5 order-1">
          <div className="relative gold-foil-edge rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl aspect-[16/10] sm:aspect-[4/3] lg:aspect-[4/5] max-w-md mx-auto lg:mx-0">
            <img
              src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800&q=70&auto=format&fit=crop"
              srcSet="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=400&q=70&auto=format&fit=crop 400w, https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800&q=70&auto=format&fit=crop 800w"
              sizes="(max-width: 640px) 100vw, 40vw"
              alt="Slow-dried single-ingredient dog treats from Rare Locals"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/40 via-transparent to-transparent" />
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-brand-dark text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full">
              <PawPrint size={12} strokeWidth={2.5} /> New Range
            </div>
          </div>
        </div>

        {/* Copy side */}
        <div className="lg:col-span-7 order-2">
          <div className="inline-flex items-center gap-2 text-brand-gold text-[11px] uppercase tracking-[0.24em] font-semibold mb-2.5">
            <PawPrint size={14} strokeWidth={2.5} />
            For your best friend
          </div>
          <h2 className="font-serif-brand text-2xl sm:text-4xl lg:text-5xl text-brand-dark leading-[1.08]">
            Small-batch pet food,<br />made with the same love.
          </h2>
          <p className="mt-3 sm:mt-5 text-brand-dark/75 text-sm sm:text-[15px] leading-relaxed max-w-xl">
            Because your dog deserves the same care we put into our coffee. From
            farm-sourced biscuits to slow-dried chicken jerky — every recipe is
            clean, honest and made in small kitchens we know by name.
          </p>

          {/* Mobile: compact 3-up row | Desktop: roomier cards */}
          <ul className="mt-5 sm:mt-7 grid grid-cols-3 gap-2.5 sm:gap-4 max-w-2xl">
            {perks.map(({ Icon, title, text }) => (
              <li key={title} className="bg-white/70 backdrop-blur border border-brand-gold/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-brand-gold/15 flex items-center justify-center text-brand-gold mb-2 sm:mb-2.5">
                  <Icon size={15} strokeWidth={2.25} />
                </div>
                <div className="text-[11px] sm:text-sm font-semibold text-brand-dark leading-tight">{title}</div>
                <p className="mt-1 text-[10px] sm:text-xs leading-relaxed text-brand-dark/70 hidden sm:block">{text}</p>
              </li>
            ))}
          </ul>

          <div className="mt-5 sm:mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/shop/pet-food"
              data-testid="pet-shop-cta"
              className="inline-flex items-center gap-2 h-10 sm:h-11 px-5 sm:px-6 rounded-full bg-brand-dark text-brand-gold hover:bg-brand-dark/90 text-xs sm:text-sm font-medium tracking-wide transition-colors"
            >
              Shop Pet Food
              <ChevronRight size={16} />
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 h-10 sm:h-11 px-3 sm:px-4 text-brand-dark hover:text-brand-gold text-xs sm:text-sm font-medium"
            >
              Read our sourcing story
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}



const GoldRule = () => <div className="gold-foil-rule gold-foil-rule--soft" aria-hidden="true" />;

function TrustStrip() {
  const items = [
    { Icon: Leaf, title: 'Small-batch & fresh', sub: 'Roasted, dried and packed to order' },
    { Icon: Truck, title: 'Free shipping over ₹500', sub: 'Pan-India, from Bengaluru' },
    { Icon: ShieldCheck, title: 'Secure payments', sub: 'UPI, cards & Cash on Delivery' },
    { Icon: RefreshCw, title: 'Easy returns', sub: 'Refunds in 5–10 business days' },
  ];
  return (
    <section data-testid="trust-strip" className="relative bg-brand-dark border-t border-brand-gold/30">
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
        {items.map(({ Icon, title, sub }, i) => (
          <div key={title} className={`flex items-center gap-2.5 sm:gap-3 py-3 sm:py-4 px-3 sm:px-5 border-brand-gold/15 ${i % 2 === 1 ? 'border-l' : ''} ${i === 2 ? 'lg:border-l' : ''} ${i < 2 ? 'border-b lg:border-b-0' : ''}`}>
            <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-brand-gold/40 text-brand-gold flex items-center justify-center flex-shrink-0">
              <Icon size={15} strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] sm:text-[13px] font-semibold text-brand-gold-light leading-tight">{title}</div>
              <div className="hidden sm:block text-[11px] text-brand-gold-light/60 leading-tight mt-0.5">{sub}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-brand-gold/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-brand-gold-light/85" data-testid="credential-chips">
          {[
            ['Registered Indian Company', `CIN ${BRAND.registrations.cin}`],
            ['DPIIT-recognized Startup', `Recognition No. ${BRAND.registrations.dpiit}`],
            ['GST Registered', `GSTIN ${BRAND.registrations.gstin}`],
          ].map(([label, detail], i) => (
            <React.Fragment key={label}>
              {i > 0 && <span className="text-brand-gold/50" aria-hidden="true">•</span>}
              <Link to="/company-registrations" className="inline-flex items-center gap-1.5 hover:text-brand-gold transition-colors" title={detail}>
                <BadgeCheck size={12} className="text-brand-gold" strokeWidth={2.2} /> {label}
              </Link>
            </React.Fragment>
          ))}
        </div>
      </div>
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-gold/70 to-transparent" />
    </section>
  );
}

function CategoryCards({ categories }) {
  // Only show top-level categories on the homepage grid
  const topLevel = (categories || []).filter((c) => !c.parent_slug);
  return (
    <section className="bg-brand-ivory/60" data-testid="category-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-16">
        <div className="flex items-end justify-between gap-4 mb-5 sm:mb-10">
          <div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-1.5 sm:mb-2">Explore</div>
            <h2 className="font-serif-brand text-2xl sm:text-4xl text-brand-dark leading-tight">Shop by Category</h2>
          </div>
          <Link to="/shop" className="text-xs sm:text-sm text-brand-green hover:text-brand-gold whitespace-nowrap pb-1" data-testid="category-view-all">View all →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 sm:gap-4">
          {topLevel.map((c, i) => {
            const isOddLast = topLevel.length % 2 === 1 && i === topLevel.length - 1;
            return (
            <Link
              key={c.id}
              to={`/shop/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className={`group card-lift gold-foil-edge relative rounded-xl sm:rounded-2xl overflow-hidden bg-brand-dark block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold ${isOddLast ? 'col-span-2 aspect-[2/1] sm:col-span-1 sm:aspect-[3/4]' : 'aspect-square sm:aspect-[3/4]'}`}
            >
              <div className="img-zoom h-full">
                <img
                  src={imgUrl(c.image, 480)}
                  srcSet={imgSrcSet(c.image, 800)}
                  sizes="(max-width: 640px) 50vw, 20vw"
                  alt={c.name}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                />
              </div>
              {/* Strong dark scrim to guarantee text legibility over any image */}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/60 to-brand-dark/10" />
              <div className="absolute inset-x-0 bottom-0 p-2.5 sm:p-4">
                <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.25em] text-brand-gold mb-0.5 sm:mb-1 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">Category</div>
                <h3 className="font-serif-brand text-base sm:text-xl text-white leading-tight [text-shadow:0_2px_6px_rgba(0,0,0,0.75)]">{c.name}</h3>
                <div className="mt-1.5 inline-flex items-center text-brand-gold text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]">
                  Shop {c.name} <ChevronRight size={12} className="ml-0.5" />
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProductsRow({ title, subtitle, items }) {
  if (!items?.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
      <div className="flex items-end justify-between mb-4 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-1">{subtitle}</div>
          <h2 className="font-serif-brand text-xl sm:text-3xl md:text-4xl text-brand-dark">{title}</h2>
        </div>
        <Link to="/shop" className="text-xs md:text-sm text-brand-green hover:text-brand-gold pb-0.5">View all →</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
        {items.slice(0, 8).map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 5500);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="bg-brand-ivory py-10 sm:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-2">Testimonials</div>
        <h2 className="font-serif-brand text-xl sm:text-3xl md:text-4xl text-brand-dark section-heading">What Our Customers Say</h2>
        <p className="mt-3 text-brand-green/80 text-xs sm:text-sm">Real experiences from people who enjoy Rare Locals every day.</p>
        <div className="mt-8 sm:mt-10 relative min-h-[200px] sm:min-h-[180px]">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <Quote className="mx-auto text-brand-gold" size={26} />
              <div className="mt-3 flex justify-center gap-1">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} size={13} className={`${k < Math.round(t.rating) ? 'fill-brand-gold text-brand-gold' : 'text-brand-gold/30'}`} />
                ))}
              </div>
              <p className="font-serif-brand italic text-base sm:text-lg md:text-xl text-brand-dark mt-4 leading-relaxed px-2">“{t.text}”</p>
              <div className="mt-4 text-sm font-medium text-brand-dark">{t.name}</div>
              <div className="text-xs text-brand-green/60">Verified Customer</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-center gap-2">
          {TESTIMONIALS.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`h-2 w-2 rounded-full ${i === idx ? 'bg-brand-gold' : 'bg-brand-gold/30'}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { icon: Truck, title: 'Free Shipping Pan-India', desc: 'Enjoy free Pan India shipping on orders above ₹500.' },
    { icon: RefreshCw, title: 'Hassle-Free Refunds', desc: 'Refunds processed within 5–10 business days after approval.' },
    { icon: Headphones, title: 'Support Online', desc: 'Monday to Saturday, 9:30 AM – 6:30 PM. We’re here to help.' },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {items.map((it, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 gold-foil-edge card-lift">
            <div className="w-11 h-11 rounded-full bg-brand-dark flex items-center justify-center text-brand-gold">
              <it.icon size={20} />
            </div>
            <h3 className="mt-4 font-serif-brand text-xl text-brand-dark">{it.title}</h3>
            <p className="mt-1 text-sm text-brand-green/80">{it.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PromoBanner() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="relative gold-foil-edge rounded-2xl sm:rounded-3xl overflow-hidden bg-brand-dark">
        <img
          src="https://images.unsplash.com/photo-1683506684881-efbb5203eacf?w=1000&q=70&auto=format&fit=crop"
          srcSet="https://images.unsplash.com/photo-1683506684881-efbb5203eacf?w=600&q=70&auto=format&fit=crop 600w, https://images.unsplash.com/photo-1683506684881-efbb5203eacf?w=1000&q=70&auto=format&fit=crop 1000w, https://images.unsplash.com/photo-1683506684881-efbb5203eacf?w=1400&q=70&auto=format&fit=crop 1400w"
          sizes="100vw"
          alt="promo"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative px-6 sm:px-8 md:px-14 py-10 sm:py-14 text-brand-gold-light max-w-2xl">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-2">Get Exclusive Deals</div>
          <h2 className="font-serif-brand text-xl sm:text-3xl md:text-4xl text-white">Fresh drops. First look. Only for insiders.</h2>
          <p className="mt-3 text-brand-gold-light/80 text-xs sm:text-sm">Subscribe to receive updates on new harvests, limited micro-lots and members-only offers.</p>
          <form onSubmit={(e) => e.preventDefault()} className="mt-5 sm:mt-6 flex flex-col sm:flex-row gap-3">
            <input type="email" placeholder="you@example.com" className="flex-1 h-11 sm:h-12 px-4 rounded-full bg-white/10 border border-brand-gold-light/30 text-brand-gold-light placeholder:text-brand-gold-light/50 outline-none focus:border-brand-gold text-sm" />
            <Button className="btn-gold rounded-full h-11 sm:h-12 px-6 sm:px-8">Subscribe</Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="text-center mb-6 sm:mb-10">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-2">Help</div>
        <h2 className="font-serif-brand text-2xl sm:text-4xl text-brand-dark section-heading">Frequently Asked Questions</h2>
      </div>
      <Accordion type="single" collapsible className="space-y-2">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl gold-foil-edge px-5">
            <AccordionTrigger className="text-left text-brand-dark hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-brand-green/80">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function BlogPreview({ posts }) {
  if (!posts?.length) return null;
  return (
    <section className="bg-brand-cream py-14 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-1">From the Journal</div>
            <h2 className="font-serif-brand text-xl sm:text-3xl md:text-4xl text-brand-dark">Stories worth brewing over</h2>
          </div>
          <Link to="/blog" className="hidden md:inline text-sm text-brand-green hover:text-brand-gold">All articles →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {posts.slice(0, 3).map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="card-lift gold-foil-edge group bg-white rounded-2xl overflow-hidden">
              <div className="img-zoom aspect-video bg-brand-ivory">
                <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-brand-gold">
                  <span className="capitalize">{p.category}</span>
                  <span className="text-brand-green/40">•</span>
                  <span className="text-brand-green/70 normal-case tracking-normal">{p.read_time} min read</span>
                </div>
                <h3 className="font-serif-brand text-lg sm:text-xl text-brand-dark mt-2 leading-snug line-clamp-2 group-hover:text-brand-green">{p.title}</h3>
                <p className="text-sm text-brand-green/80 mt-2 line-clamp-3">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center md:hidden">
          <Link to="/blog" className="text-sm text-brand-green underline">Read more articles</Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  useSeo({
    title: 'Rare Locals | Discover Rare, Support Local',
    description: 'Discover distinctive coffee, tea and naturally prepared ingredients sourced from local regions across India. Discover Rare. Support Local.',
    canonicalPath: '/',
  });
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [hotDeals, setHotDeals] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    Banners.list().then(setBanners).catch(() => {});
    Categories.list().then(setCategories).catch(() => {});
    Products.list({ best_seller: true, limit: 8 }).then((d) => setBestSellers(d.items || [])).catch(() => {});
    Products.list({ hot_deal: true, limit: 8 }).then((d) => setHotDeals(d.items || [])).catch(() => {});
    Blog.list({ limit: 3 }).then((d) => setPosts(d.items || [])).catch(() => {});
  }, []);

  return (
    <div>
      <HeroCarousel banners={banners} />
      <TrustStrip />
      <CategoryCards categories={categories} />
      <GoldRule />
      <ProductsRow title="Best Sellers" subtitle="Loved by customers" items={bestSellers} />
      <GoldRule />
      <PromoBanner />
      <GoldRule />
      <ProductsRow title="Hot Deals" subtitle="Limited time offers" items={hotDeals} />
      <GoldRule />
      <PetFoodSpotlight />
      <GoldRule />
      <BlogPreview posts={posts} />
      <GoldRule />
      <InstagramFeed handle="rarelocals" />
      <GoldRule />
      <Testimonials />
      <GoldRule />
      <Features />
      <GoldRule />
      <FAQ />
    </div>
  );
}
